<template>
    <DashboardContentLayout :heading="$t('views.monitoring.activeGroups.headings.title')" has-pig-type-picker slim-pig-type-picker>
        <template #header-top-right>
            <button
                type="button"
                class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-default disabled:opacity-50"
                :disabled="isRefreshing"
                :title="$t('views.monitoring.activeGroups.texts.refresh')"
                @click="refreshOverview"
            >
                <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': isRefreshing }" aria-hidden="true" />
                <span>{{ $t('views.monitoring.activeGroups.texts.refresh') }}</span>
            </button>
        </template>
        <div class="border-t border-t-gray-200 mt-5 mb-6" aria-hidden="true" />

        <!-- Controls: inline farm chip picker (one pill per valid farm, all active by default) + inline sort button group. -->
        <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div class="flex min-w-0 flex-1 items-center gap-3">
                <span class="shrink-0 font-semibold">{{ $t('views.monitoring.activeGroups.fields.farm') }}:</span>
                <!-- One pill button per farm in the user's valid scope (farms with at least one barn of
                     the current pig type); FarmChipPicker owns sorting, the "+N" overflow cutoff and its
                     popover. -->
                <FarmChipPicker :farms="validFarms" v-model="selectedFarmIds" />
            </div>

            <!-- Dynamic Mortality view toggle: switches the whole table between the 7-day rolling and
                 since-Production Group-start cumulative figures at once. Right-aligned. -->
            <div class="ml-auto flex items-center gap-2">
                <span class="text-xs font-medium text-gray-400">{{ $t('views.monitoring.activeGroups.mortalityView.label') }}:</span>
                <div class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                    <button
                        v-for="option in mortalityViewOptions"
                        :key="option"
                        type="button"
                        class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                        :class="mortalityView === option ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100'"
                        @click="mortalityView = option"
                    >
                        {{ $t(`views.monitoring.activeGroups.mortalityView.${option}`) }}
                    </button>
                </div>
            </div>

            <!-- Sort control: secondary label. Every key orders *within* the active → empty → no-Production Group
                 tiers (see sortSections.ts), so none of them is a literal ordering of the whole list — most
                 visibly "Sektion", where a user reasonably expects a plain 1..8. The hint says so once on the
                 group rather than qualifying each button, which would crowd a three-option toggle. -->
            <div class="flex items-center gap-2" :title="$t('views.monitoring.activeGroups.sort.tierHint')">
                <span class="text-xs font-medium text-gray-400">{{ $t('views.monitoring.activeGroups.fields.sort') }}:</span>
                <div class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                    <button
                        v-for="option in sortOptions"
                        :key="option"
                        type="button"
                        class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                        :class="sortKey === option ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100'"
                        @click="sortKey = option"
                    >
                        {{ $t(`views.monitoring.activeGroups.sort.${option}`) }}
                    </button>
                </div>
            </div>
        </div>

        <!-- ActionModal for alarm form handling. Wraps the section grid so the `open` slot callback
             is accessible to the alarm chip click handlers directly in each row. -->
        <ActionModal :initial-step-index="1" :initial-state="alarmActionState" :step-config="alarmStepConfig" :header="alarmActionHeader" @closing="onAlarmFormClosing">
        <template #default="{ open: openAlarmModal }">

        <FadeTransition mode="out-in">
            <div v-if="isLoading" class="flex items-center justify-center px-4 py-16">
                <Spinner class="h-10 w-10" />
            </div>
            <div v-else-if="farmGroups.length" key="content" class="mt-8 overflow-x-auto">
                <div class="flex min-w-[1064px] flex-col">
                    <!-- Farm groups: border-top separator between farms, then barn sub-groups each with a
                         combined "Farm · Barn" headline. -->
                    <template v-for="(farm, farmIndex) in farmGroups" :key="farm.farmId">
                        <div :class="farmIndex > 0 ? 'mt-8 border-t-2 border-gray-200 pt-5' : ''">
                            <!-- Barn sub-groups. -->
                            <div
                                v-for="barn in farm.barns"
                                :key="`${farm.farmId}-${barn.barnName}`"
                                class="mb-5 last:mb-0"
                            >
                                <!-- Combined Farm · Barn headline with section count as a muted suffix. -->
                                <div class="mb-2 flex items-baseline gap-2 px-3">
                                    <span class="text-[14.5px] font-extrabold text-gray-900">{{ farm.farmName }} &middot; {{ barn.barnName }}</span>
                                    <span class="text-xs font-medium text-gray-400">
                                        {{ $tc('views.monitoring.activeGroups.tables.sections', barn.sections.length) }}
                                    </span>
                                </div>

                                <!-- Barn-level KPI aggregate strip: total pig count (every section in the barn)
                                     and dead count under the active page-level mortality view (7-day rolling
                                     or since-Production Group-start cumulative — same toggle as the Døde column
                                     below). Both fall back to placeholder until the barn-kpis query resolves.
                                     Dead alone carries a caveat: unlike the pig count it only covers sections
                                     that contributed a mortality window, so `sections_suppressed` says how
                                     many are missing from it. -->
                                <div class="mb-2.5 flex items-center gap-5 px-3 text-[11px] text-gray-400">
                                    <span class="flex items-baseline gap-1.5">
                                        <span class="font-medium">{{ $t('views.monitoring.activeGroups.barnAggregate.pigs') }}</span>
                                        <span
                                            class="font-semibold tabular-nums"
                                            :class="barnKpiByBarnId.get(barn.barnId)?.pig_count != null ? 'text-gray-600' : 'text-gray-300'"
                                        >{{ barnKpiByBarnId.get(barn.barnId)?.pig_count != null ? $n(barnKpiByBarnId.get(barn.barnId)!.pig_count!, 'decimal') : placeholder }}</span>
                                    </span>
                                    <span class="flex items-baseline gap-1.5">
                                        <span class="font-medium">{{ $t('views.monitoring.activeGroups.barnAggregate.dead') }}</span>
                                        <span
                                            class="font-semibold tabular-nums"
                                            :class="barnDeadCount(barn.barnId) != null ? 'text-gray-600' : 'text-gray-300'"
                                        >{{ barnDeadCount(barn.barnId) != null ? $n(barnDeadCount(barn.barnId)!, 'decimal') : placeholder }}</span>
                                        <span
                                            v-if="barnSectionsSuppressed(barn.barnId) > 0"
                                            class="cursor-help italic text-gray-300"
                                            :title="$t('views.monitoring.activeGroups.barnAggregate.deadPartialTooltip')"
                                        >{{ $tc('views.monitoring.activeGroups.barnAggregate.deadPartial', barnSectionsSuppressed(barn.barnId)) }}</span>
                                    </span>
                                </div>

                                <!-- Column header sits directly above the section cards in each barn group.
                                     px-3 is on each cell (not the container) to match data cell padding exactly. -->
                                <div class="section-grid-row mb-1.5 text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                                    <div class="px-3">{{ $t('views.monitoring.activeGroups.tables.headings.section') }}</div>
                                    <div class="px-3">{{ $t('views.monitoring.activeGroups.tables.headings.age') }}</div>
                                    <div class="px-3">{{ $t('views.monitoring.activeGroups.tables.headings.weight') }}</div>
                                    <div class="px-3">{{ $t('views.monitoring.activeGroups.tables.headings.pigs') }}</div>
                                    <div class="px-3">{{ $t('views.monitoring.activeGroups.tables.headings.dead') }}</div>
                                    <div class="px-3">{{ $t('views.monitoring.activeGroups.tables.headings.dailyGain') }}</div>
                                    <div class="px-3">{{ $t('views.monitoring.activeGroups.tables.headings.growthIndex') }}</div>
                                    <div class="px-3">{{ $t('views.monitoring.activeGroups.tables.headings.waterIndex') }}</div>
                                    <!-- Chart icon column: no heading. -->
                                    <div aria-hidden="true" />
                                </div>

                                <!-- Section cards (active → ghost → inactive, sorted by sortSections). -->
                                <div class="flex flex-col gap-[6px]">
                                    <div
                                        v-for="section in barn.sections"
                                        :key="section.id"
                                        class="section-grid-row items-center rounded-[9px] border border-l-4 text-sm transition-shadow duration-150"
                                        :class="[cardClasses(section), { 'section-recently-updated': changedSectionIds.has(section.id) }]"
                                    >
                                        <!-- Section name (always shown). Active/ghost rows: the whole cell is clickable and
                                             opens the manage Production Group modal (replaces the old dedicated edit-pencil
                                             button, freeing up space). Inactive rows aren't clickable here — the
                                             "Start Production Group" pill in the KPI columns is the affordance instead. -->
                                        <button
                                            v-if="section.hasProductionGroup"
                                            type="button"
                                            class="flex w-full min-w-0 items-center gap-2 rounded-md px-3 py-[9px] text-left transition-colors hover:bg-gray-200"
                                            :title="$t('views.monitoring.productionGroups.modals.manageProductionGroup.editTitle')"
                                            :aria-label="`${$t('views.monitoring.productionGroups.modals.manageProductionGroup.editTitle')}: ${section.sectionName}`"
                                            @click.stop="openManageProductionGroup(section)"
                                        >
                                            <span
                                                class="min-w-0 flex-1 truncate"
                                                :title="section.productionGroupName ? `${section.sectionName} · ${section.productionGroupName}` : section.sectionName"
                                            ><span
                                                class="text-[13.5px] font-semibold"
                                                :class="!section.isEmpty ? 'text-gray-900' : 'text-gray-400'"
                                            >{{ section.sectionName }}</span><span
                                                v-if="section.productionGroupName"
                                                class="text-[11.5px] font-normal text-gray-400"
                                            > · {{ section.productionGroupName }}</span></span>
                                        </button>
                                        <div v-else class="flex min-w-0 items-center gap-2 px-3 py-[9px]">
                                            <span class="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-gray-400" :title="section.sectionName">{{ section.sectionName }}</span>
                                        </div>

                                        <!-- Inactive row (no Production Group). Three spans instead of one, so the
                                             Section's pig count still lands under the PIGS heading: the action
                                             pill takes the Alder + Vægt columns, the pig count its own column
                                             (secondary styling — the row is still "no active Production Group"),
                                             and the status label the remainder. Without the pig count here the
                                             barn header's total does not reconcile against its rows. -->
                                        <template v-if="!section.hasProductionGroup">
                                            <div class="flex items-center px-3 py-[9px]" style="grid-column: 2 / 4">
                                                <button
                                                    type="button"
                                                    class="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary-600 bg-primary-50 px-3 py-1 text-[11.5px] font-bold text-primary-700 transition-colors hover:bg-primary-100"
                                                    :title="$t('views.monitoring.productionGroups.modals.manageProductionGroup.createTitle')"
                                                    @click.stop="openManageProductionGroup(section)"
                                                >
                                                    <PlayCircle class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                                    {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.createTitle') }}
                                                </button>
                                            </div>
                                            <div class="px-3 py-[9px] tabular-nums font-semibold text-gray-400" style="grid-column: 4">
                                                {{ section.pigCount != null ? $n(section.pigCount, 'decimal') : placeholder }}
                                            </div>
                                            <div class="flex items-center px-3 py-[9px]" style="grid-column: 5 / 9">
                                                <span class="text-xs italic text-gray-400">{{ $t('views.monitoring.activeGroups.texts.noProductionGroup') }}</span>
                                            </div>
                                        </template>

                                        <!-- Active row KPI cells. -->
                                        <template v-else>
                                            <!-- Alder: when section has active alarms the chip becomes a red clickable button
                                                 (decoupled from GI threshold — alarm chip only fires when there are actual Alarm records). -->
                                            <div class="px-3 py-[9px]">
                                                <button
                                                    v-if="section.activeAlarms.length > 0"
                                                    type="button"
                                                    :class="ALARM_CHIP_CLASS"
                                                    @click.stop="openAlarm(section, openAlarmModal)"
                                                >
                                                    <AlertTriangle class="h-3 w-3 shrink-0" aria-hidden="true" />
                                                    <template v-if="section.day !== null">
                                                        {{ $t('views.monitoring.activeGroups.texts.week') }} {{ section.week }} &middot; {{ $t('views.monitoring.activeGroups.texts.day') }} {{ section.day }}
                                                    </template>
                                                    <ChevronRight class="h-3 w-3 shrink-0" aria-hidden="true" />
                                                </button>
                                                <span
                                                    v-else-if="section.day !== null"
                                                    class="inline-flex items-center whitespace-nowrap rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-bold text-cyan-700"
                                                >
                                                    {{ $t('views.monitoring.activeGroups.texts.week') }} {{ section.week }} &middot; {{ $t('views.monitoring.activeGroups.texts.day') }} {{ section.day }}
                                                </span>
                                                <span v-else class="text-gray-300">{{ placeholder }}</span>
                                            </div>

                                            <!-- Vægt: stop icon when no weight sensor; wifi-off icon when sensor is present
                                                 but no data is available; large bold value otherwise. -->
                                            <div class="tabular-nums">
                                                <SensorGatedKpiCell
                                                    :has-sensor="section.hasWeightSensor"
                                                    :has-value="section.latestWeightKg !== null"
                                                    :open-title="$t('views.monitoring.activeGroups.modals.weightDetail.openTitle')"
                                                    button-class="flex items-baseline gap-1"
                                                    @open="weightDetailSection = section"
                                                >
                                                    <span
                                                        class="text-[16px] font-extrabold leading-none"
                                                        :class="section.weightSource === 'Estimated' ? 'text-[#8C96A3]' : 'text-gray-900'"
                                                    >{{ $n(section.latestWeightKg!, 'weight') }}</span>
                                                    <small
                                                        class="text-[11px]"
                                                        :class="section.weightSource === 'Estimated' ? 'text-[#B6BEC9]' : 'text-gray-400'"
                                                    >{{ section.weightSource === 'Estimated' ? 'kg · est.' : 'kg' }}</small>
                                                    <DegradedCameraMarker :contributing="section.contributingWeightCameraCount" :expected="section.expectedCameraCount" />
                                                </SensorGatedKpiCell>
                                            </div>

                                            <!-- Pig count, grouped by the active i18n locale's separators. -->
                                            <div class="px-3 py-[9px] tabular-nums font-semibold text-gray-700">
                                                {{ section.pigCount != null ? $n(section.pigCount, 'decimal') : placeholder }}
                                            </div>

                                            <!-- Døde: Dynamic Mortality equivalent full-cycle rate under the active page-level
                                                 view (7-day rolling or since Production Group start). Red at or above the mortality target,
                                                 green below; dash when unavailable. Clickable whenever a Production Group is active —
                                                 opens the mortality detail modal with the numbers behind the rate, or (when the
                                                 figure is suppressed) the reason why. -->
                                            <div class="tabular-nums">
                                                <button
                                                    v-if="section.hasProductionGroup"
                                                    type="button"
                                                    class="flex w-full items-baseline gap-0.5 rounded-md px-3 py-[9px] transition-colors hover:bg-gray-200"
                                                    :title="$t('views.monitoring.activeGroups.modals.mortalityDetail.openTitle')"
                                                    @click="mortalityDetailSection = section"
                                                >
                                                    <template v-if="sectionMortalityPercent(section) !== null">
                                                        <span
                                                            class="text-[16px] font-extrabold leading-none"
                                                            :class="mortalityPercentClass(sectionMortalityAtOrAboveTarget(section))"
                                                        >{{ $n(sectionMortalityPercent(section)!, 'decimal') }}</span>
                                                        <span
                                                            class="text-[12.5px] font-medium"
                                                            :class="mortalityPercentClass(sectionMortalityAtOrAboveTarget(section))"
                                                        >%</span>
                                                        <!-- Pig-diff indicator: how many pigs too many have died (red), or how much room
                                                             is left before the target is breached (green). Same colour/format helpers as
                                                             the Objectives page's own Pigs to Target badge (objectiveDisplay.ts) - own sign
                                                             convention from the % value beside it, not mortalityPercentClass. Hidden when
                                                             null (no resolved target), not shown as a placeholder - the % itself already
                                                             says "unavailable" via its own dash. -->
                                                        <span
                                                            v-if="sectionMortalityPigsToTarget(section) !== null"
                                                            class="text-[10.5px] font-semibold"
                                                            :class="pigsToTargetColourClass(sectionMortalityPigsToTarget(section))"
                                                        >({{ formatPigsToTarget(sectionMortalityPigsToTarget(section)!) }})</span>
                                                    </template>
                                                    <span v-else class="text-[16px] font-extrabold leading-none text-gray-300">{{ placeholder }}</span>
                                                </button>
                                                <span v-else class="block px-3 py-[9px] text-[16px] font-extrabold leading-none text-gray-300">{{ placeholder }}</span>
                                            </div>

                                            <!-- Daily Gain: stop icon when no weight sensor; wifi-off icon when sensor is
                                                 present but no data is available. Marked from its OWN camera count - a
                                                 camera's newest reading holds a weight far more often than a growth, so
                                                 this figure rests on fewer cameras than the weight more often than not,
                                                 and the weight's count would say otherwise. -->
                                            <div class="tabular-nums">
                                                <SensorGatedKpiCell
                                                    :has-sensor="section.hasWeightSensor"
                                                    :has-value="section.dailyGain !== null"
                                                    :open-title="$t('views.monitoring.activeGroups.modals.weightDetail.openTitle')"
                                                    button-class="flex items-baseline gap-1"
                                                    @open="weightDetailSection = section"
                                                >
                                                    <span class="text-[16px] font-extrabold leading-none text-gray-900">{{ $n(section.dailyGain!, 'decimal') }}</span>
                                                    <small class="text-[11px] text-gray-400">{{ $t('views.monitoring.activeGroups.texts.dailyGainUnit') }}</small>
                                                    <DegradedCameraMarker :contributing="section.contributingDailyGainCameraCount" :expected="section.expectedCameraCount" />
                                                </SensorGatedKpiCell>
                                            </div>

                                            <!-- Vækstindeks: stop icon when no weight sensor; wifi-off icon when sensor is
                                                 present but no data is available; gauge bar otherwise. -->
                                            <div>
                                                <SensorGatedKpiCell
                                                    :has-sensor="section.hasWeightSensor"
                                                    :has-value="section.liveGrowthIndex !== null"
                                                    :open-title="$t('views.monitoring.activeGroups.modals.weightDetail.openTitle')"
                                                    button-class="flex items-center gap-1.5"
                                                    @open="weightDetailSection = section"
                                                >
                                                    <IndexGaugeBar :value="section.liveGrowthIndex!" />
                                                    <!-- The index needs a weight AND a growth on one reading, so it
                                                         withholds at least as often as the other two. Its own count. -->
                                                    <DegradedCameraMarker :contributing="section.contributingGrowthIndexCameraCount" :expected="section.expectedCameraCount" />
                                                </SensorGatedKpiCell>
                                            </div>

                                            <!-- Water Norm: stop icon when no water sensor; an active Production Group's
                                                 cell always opens its history. A missing headline index does not mean
                                                 the existing Water Norm analysis should be inaccessible. -->
                                            <div>
                                                <span v-if="!section.hasWaterSensor" class="block px-3 py-[9px]" :title="$t('views.monitoring.activeGroups.texts.noSensor')">
                                                    <Ban class="h-3.5 w-3.5 text-gray-300" aria-hidden="true" />
                                                </span>
                                                <button
                                                    v-else-if="section.hasProductionGroup"
                                                    type="button"
                                                    class="flex w-full items-center gap-1.5 rounded-md px-3 py-[9px] transition-colors hover:bg-gray-200"
                                                    :title="$t('views.monitoring.activeGroups.modals.waterDetail.openTitle')"
                                                    @click="waterDetailSection = section"
                                                >
                                                    <template v-if="section.waterIndex !== null">
                                                        <Droplets class="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                                                        <IndexGaugeBar :value="section.waterIndex" />
                                                    </template>
                                                    <WifiOff v-else class="h-3.5 w-3.5 text-gray-300" aria-hidden="true" />
                                                </button>
                                                <span v-else class="block px-3 py-[9px]" :title="$t('views.monitoring.activeGroups.texts.noData')">
                                                    <WifiOff class="h-3.5 w-3.5 text-gray-300" aria-hidden="true" />
                                                </span>
                                            </div>
                                        </template>

                                        <!-- Chart: the row's last column, outside both branches above, so every Section
                                             has one whatever state it is in. A Section with no running Production Group needs
                                             a way in too: prev/next inside the overlay walks to it regardless, and a
                                             reader must not be able to arrive somewhere they could not have clicked. The
                                             chart is not empty for such a Section either: dead counts, Notes, Alarms and
                                             any earlier Production Group's bands all still draw. -->
                                        <div class="flex items-center justify-center py-[9px] pr-2" style="grid-column: 9">
                                            <button
                                                type="button"
                                                class="grid h-7 w-7 shrink-0 place-content-center rounded-md border border-gray-200 bg-white text-gray-400 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-600"
                                                :title="$t('views.monitoring.activeGroups.texts.openChart')"
                                                @click="openSection(section)"
                                            >
                                                <LineChart class="h-4 w-4" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div v-else key="empty" class="flex items-center justify-center px-4 py-16">
                <div class="text-center text-gray-500">{{ $t('views.monitoring.activeGroups.texts.noSections') }}</div>
            </div>
        </FadeTransition>

        <!-- Multi-alarm list modal: only opens when a section has >1 active alarm. For single alarms
             the alarm chip calls loadAndOpenAlarm() directly, skipping this modal. -->
        <AlarmHandleModal
            :open="alarmListModalOpen"
            :alarms="alarmListAlarms"
            :is-loading="isLoadingAlarmForm"
            @close="alarmListModalOpen = false"
            @select-alarm="(id) => loadAndOpenAlarm(id, openAlarmModal)"
        />

        <!-- Mortality detail modal: the figures behind the clicked Døde rate (both windows) plus the
             on-demand pig-tally chart. Parent-controlled like the overlay; lives inside the ActionModal
             slot so its alarm chip can reach the same `open` callback the table's chips use. -->
        <MortalityDetailModal
            :section="mortalityDetailSection"
            :sections="mortalityDetailSections"
            :view="mortalityView"
            @close="mortalityDetailSection = null"
            @navigate="mortalityDetailSection = $event"
            @handle-alarm="(section) => openAlarmFromMortalityDetail(section, openAlarmModal)"
            @edit-target="onEditTargetFromMortalityDetail"
        />

        <!-- Water detail modal: the figures behind the clicked Water Index plus the on-demand
             water-norm chart. Parent-controlled like the mortality modal. -->
        <WaterDetailModal
            :section="waterDetailSection"
            :sections="waterDetailSections"
            @close="waterDetailSection = null"
            @navigate="waterDetailSection = $event"
            @handle-alarm="(section) => openAlarmFromWaterDetail(section, openAlarmModal)"
        />

        <!-- Weight detail modal: the figures behind the clicked Vægt/Tilvækst/Vækstindeks cell plus
             the on-demand weight/growth charts. Parent-controlled like the other detail modals. -->
        <WeightDetailModal
            :section="weightDetailSection"
            :sections="weightDetailSections"
            @close="weightDetailSection = null"
            @navigate="weightDetailSection = $event"
            @handle-alarm="(section) => openAlarmFromWeightDetail(section, openAlarmModal)"
        />

        <!-- Fullscreen section detail overlay, controlled entirely by parent state. It shares the KPI
             modals' header component, so it needs the same `sections` display-order list and alarm
             hand-off as those modals - hence it lives inside this slot too. -->
        <SectionDetailOverlay
            :section="selectedSection"
            :sections="allDetailSections"
            :production-group-history="selectedSectionProductionGroupHistory"
            :production-group-history-unavailable="isComparisonHistoryError"
            @close="closeSection"
            @navigate="navigateToSection"
            @handle-alarm="(section) => openAlarmFromSectionDetail(section, openAlarmModal)"
        />
        </template>
        </ActionModal>

        <!-- Manage Production Group modal: create (no active Production Group) or edit/end (active). Self-contained —
             on success it refreshes the page's Production Group + live-KPI queries and closes itself. -->
        <ManageProductionGroupModal
            v-model="isManageModalOpen"
            :target="manageRow"
            :production-group="manageProductionGroup"
            :extra-invalidate-query-key-roots="manageModalInvalidateQueryKeyRoots"
        />

        <!-- Targets modal, shared with the Objectives page - opened from the mortality detail modal's
             "Edit target" button (see targetsModal/onEditTargetFromMortalityDetail). -->
        <ManageObjectivesModal ref="targetsModal" :is-weaner="isWeaners" :show-trigger="false" @saved="onTargetSaved" />
    </DashboardContentLayout>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { differenceInCalendarDays, format, parseISO, subMonths } from 'date-fns';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import Spinner from '@/components/ui/Spinner.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { useSectionLiveKpis, useBarnLiveKpis, queryKeys as activeGroupsQueryKeys } from '@/plugins/store/actions/queries/active-groups';
import { useBarnProductionGroups, useProductionGroupTimeline, type ProductionGroupTimelineRequest } from '@/plugins/store/actions/queries/production-groups';
import { PigType, type Section, type ProductionGroup, type ProductionGroupBarnResponse } from '@/types';
import { AlertTriangle, Ban, ChevronRight, Droplets, LineChart, PlayCircle, RefreshCw, WifiOff } from 'lucide-vue-next';
import SectionDetailOverlay from './SectionDetailOverlay.vue';
import AlarmHandleModal from './AlarmHandleModal.vue';
import MortalityDetailModal from './MortalityDetailModal.vue';
import WaterDetailModal from './WaterDetailModal.vue';
import WeightDetailModal from './WeightDetailModal.vue';
import { indexColourClass, INDEX_COLOUR_RED, INDEX_COLOUR_GREEN, INDEX_COLOUR_NEUTRAL } from './indexColour';
import { ALARM_CHIP_CLASS } from './alarmChipStyle';
import ManageProductionGroupModal from '../production-groups/ManageProductionGroupModal.vue';
import ManageObjectivesModal from '@/components/common/objectives/ManageObjectivesModal.vue';
import { formatPigsToTarget, pigsToTargetColourClass } from '@/views/dashboard/overview/objectives/objectiveDisplay';
import SensorGatedKpiCell from './SensorGatedKpiCell.vue';
import DegradedCameraMarker from './DegradedCameraMarker.vue';
import IndexGaugeBar from './IndexGaugeBar.vue';
import FarmChipPicker from './FarmChipPicker.vue';
import { mortalityAtOrAboveTargetForView, mortalityPercentForView, mortalityPigsToTargetForView, type MortalityView, type SectionRow } from './sectionRow';
import { DEFAULT_SORT_KEY, sortAndGroupSectionsByFarm, type SectionSortKey } from './sortSections';
import { useAlarmModal } from '@/composables/useAlarmModal';
import { useSectionRows } from '@/composables/useSectionRows';

const route = useRoute();
const router = useRouter();

const placeholder = '—';

const sitesAreLoaded = computed(() => store.state.sitesAreLoaded);
// getSelectedPeriodByDate (plain from/to yyyy-MM-dd), not getSelectedPeriod - matches the query
// string useBarnProductionGroups forwards to GetProductionGroups.
const selectedPeriod = computed(() => store.getters.getSelectedPeriodByDate);
const isWeaners = computed(() => store.state.pigType === PigType.Weaners);

// The all-sections data source (Module C): every physical Section in the selected barns. Fetch it on a
// fresh session if it has not been loaded yet, and gate rendering on it being available.
const sectionsAreLoaded = computed(() => store.state.sections !== null);
onMounted(() => {
    if (store.state.sections === null) {
        store.dispatch(ActionType.GetSections, {});
    }
});

// Page-local farm picker: operates over the app's global farm scope (the globally selected farms).
const scopedFarms = computed(() => store.state.farms.filter((farm) => store.state.selectedFarmIds.includes(farm.farm_id)));

// The valid farm set for the chip picker: farms in global scope that have at least one barn of the
// current pig type. Recomputes when the global pig type toggle flips, so chips for farms with no
// barns of the new type disappear automatically.
const validFarms = computed(() =>
    scopedFarms.value.filter((farm) =>
        store.state.barns.some((barn) => barn.farm_id === farm.farm_id && barn.is_weaners === isWeaners.value)
    )
);

// Selected farm ids (v-model of FarmChipPicker below), which owns sorting, the row-fits-on-one-line
// measurement and the "+N" overflow popover.
const selectedFarmIds = ref<number[]>([]);

// Sort control state (Module B). Defaults to Alder (youngest → oldest).
const sortOptions: SectionSortKey[] = ['age', 'attention', 'section'];
const sortKey = ref<SectionSortKey>(DEFAULT_SORT_KEY);

// Page-level Dynamic Mortality view toggle. Applies to the whole table at once —
// there is no per-section switching. Defaults to cumulative mortality since Production Group start.
const mortalityViewOptions: MortalityView[] = ['rolling', 'cumulative'];
const mortalityView = ref<MortalityView>('cumulative');

// Overlay state: which Section is open is the URL's business, not this component's. The route
// carries the id (ActiveGroupsDetail), so the overlay survives a reload, can be linked to, and
// closes with the back button. The row behind that id is resolved further down, independently
// of the grid's own filters.
const openSectionId = computed(() => {
    const raw = route.params.sectionId;
    return typeof raw === 'string' ? Number(raw) : null;
});

// Mortality detail modal state: the row whose Døde cell was clicked (null = closed).
const mortalityDetailSection = ref<SectionRow | null>(null);

// Targets modal (ManageObjectivesModal, shared with the Objectives page) - opened from inside the
// mortality detail modal's own "Edit target" button, pre-focused on the clicked Section's Farm.
// `isWeaner` passes straight through from this page's own pig-type filter (see isWeaners above) -
// same global store value the Objectives page's own instance reads, so no separate lookup is needed.
const targetsModal = ref<InstanceType<typeof ManageObjectivesModal> | null>(null);
// The Section the target-edit affordance was opened for - captured separately from
// mortalityDetailSection (cleared below before the Targets modal opens, so the two never stack) so
// onTargetSaved still knows which Section's live KPI to refresh once the save lands.
const targetEditSection = ref<SectionRow | null>(null);

// Same z-index hand-off as the alarm flow above: the Targets modal and the mortality modal would
// otherwise paint at conflicting stacking contexts, so the mortality modal closes first.
function onEditTargetFromMortalityDetail(section: SectionRow): void {
    mortalityDetailSection.value = null;
    targetEditSection.value = section;
    targetsModal.value?.openForFarm(section.farmId);
}

// "Setting it, should trigger a re-retrieval of the data, so the mortality figures update on the
// section in question" (ticket text) - refreshes only targetEditSection's own live KPI (see
// targetSavedSectionIds/refetchTargetSavedLiveKpi above), not the whole visible batch.
async function onTargetSaved(): Promise<void> {
    const section = targetEditSection.value;
    if (!section) return;
    targetSavedSectionIds.value = [section.id];
    await refetchTargetSavedLiveKpi();
}

// Water detail modal state: the row whose Vand cell was clicked (null = closed).
const waterDetailSection = ref<SectionRow | null>(null);

// Weight detail modal state: the row whose Vægt/Tilvækst/Vækstindeks cell was clicked (null = closed).
const weightDetailSection = ref<SectionRow | null>(null);

// Manage Production Group modal state. `manageRow` is the row whose manage button was clicked (drives
// create vs edit mode via its `productionGroupId`); `manageProductionGroup` is the active Production Group for that
// row (edit mode supplies its existing name + dates).
const isManageModalOpen = ref(false);
const manageRow = ref<SectionRow | null>(null);
const manageProductionGroup = ref<ProductionGroup | null>(null);

// Every query this page caches per-Section that a save/recompute in the modal can change: live KPIs,
// the barn KPI rollup, and the three detail-chart series. Passed to the modal so it can invalidate
// them without needing to know Active groups' own cache shape.
const manageModalInvalidateQueryKeyRoots = [
    activeGroupsQueryKeys.getSectionLiveKpisRoot,
    activeGroupsQueryKeys.getBarnLiveKpisRoot,
    activeGroupsQueryKeys.getPigTallySeriesRoot,
    activeGroupsQueryKeys.getWaterNormSeriesRoot,
    activeGroupsQueryKeys.getWeightSeriesRoot,
];

// Alarm modal state and orchestration (extracted to composable).
const {
    alarmActionState,
    alarmStepConfig,
    alarmActionHeader,
    alarmListModalOpen,
    alarmListAlarms,
    isLoadingAlarmForm,
    loadAndOpenAlarm,
    openAlarm,
    onAlarmFormClosing,
} = useAlarmModal();

// Pig-type toggle always resets to all valid farms - a farm with barns of both types stays in
// `validFarms` across the flip, so filtering the old selection against it would silently carry
// the selection over instead of resetting it for the new type.
watch(isWeaners, () => {
    selectedFarmIds.value = validFarms.value.map((farm) => farm.farm_id);
});

// Default to all valid farms, and keep the selection valid as the global farm scope changes. Drop
// selected farms that fall out of scope; if that empties the selection, re-default to all valid
// farms. Deselecting a chip by hand leaves the (possibly empty) selection alone.
watch(
    scopedFarms,
    () => {
        const ids = validFarms.value.map((farm) => farm.farm_id);
        const valid = selectedFarmIds.value.filter((id) => ids.includes(id));
        selectedFarmIds.value = valid.length ? valid : ids;
    },
    { immediate: true }
);

// Close a stale modal whenever the farm selection changes, so its snapshot never lingers. The
// detail overlay is deliberately not closed here any more: it is the route's, and a link to a
// Section has to open it whatever the farm chips happen to be filtered to.
watch(selectedFarmIds, () => {
    mortalityDetailSection.value = null;
    waterDetailSection.value = null;
    weightDetailSection.value = null;
});

const selectedFarms = computed(() => validFarms.value.filter((farm) => selectedFarmIds.value.includes(farm.farm_id)));

// Lookups to map production-group barn entries back to their Farm for the Farm → Barn grouping.
const farmsById = computed(() => new Map(store.state.farms.map((farm) => [farm.farm_id, farm])));
const barnsById = computed(() => new Map(store.state.barns.map((barn) => [barn.barn_id, barn])));

// Barns across all selected farms, filtered by the global PigType (animal group).
const farmBarns = computed(() =>
    store.state.barns.filter((barn) => selectedFarmIds.value.includes(barn.farm_id) && barn.is_weaners === isWeaners.value)
);

const barnIds = computed(() => farmBarns.value.map((barn) => barn.barn_id));
const isQueryEnabled = computed(() => barnIds.value.length > 0);

const { data: productionGroupBarns, isLoading: isProductionGroupsLoading } = useBarnProductionGroups({ barnIds, selectedPeriod }, { enabled: isQueryEnabled });

// All physical Sections in the selected barns (Module C, pass 1 source). `farmBarns`/`barnIds` already
// filter by the selected farms and the global pig type, so the barn-id membership test carries both.
// Declared ahead of the live-KPI query below because that query is addressed by section and takes
// its section ids from here.
const physicalSections = computed<Section[]>(() => {
    if (!store.state.sections) {
        return [];
    }
    const barnIdSet = new Set(barnIds.value);
    return store.state.sections.filter((section) => barnIdSet.has(section.barn_id));
});

// Single batch query for every section on the page, addressed by section
// rather than by barn. The section list is already client-side and already scoped to the selected
// farms and the active PigType, so no extra request is needed to discover the ids and the server
// resolves nothing from barns. Stays a computed so the query re-runs when the selection changes.
const sectionIds = computed(() => physicalSections.value.map((section) => section.id));
const { data: liveKpisData, isLoading: isLiveKpisLoading, isFetching: isLiveKpisFetching, refetch: refetchLiveKpis } = useSectionLiveKpis(sectionIds);
const liveKpis = computed(() => liveKpisData.value ?? []);

// Barn aggregate KPIs: pig count + dead (last 7 days) per barn.
const { data: barnKpisData, isFetching: isBarnKpisFetching, refetch: refetchBarnKpis } = useBarnLiveKpis(barnIds);
const barnKpiByBarnId = computed(() => new Map((barnKpisData.value ?? []).map((k) => [k.barn_id, k])));

// A single-Section KPI re-read for right after a mortality-target save (see targetsModal/onTargetSaved
// below) - its own independently-keyed query rather than a call to refetchLiveKpis(), which would
// refetch the whole visible batch. The ticket is explicit: saving updates "the section in question",
// not every row on screen - siblings self-correct on the existing 5-minute poll. Mirrors the
// linkedSectionIds/linkedLiveKpisData pattern further down for the same reason (a second,
// independently-keyed useSectionLiveKpis call over a single id).
const targetSavedSectionIds = ref<number[]>([]);
const { data: targetSavedLiveKpisData, refetch: refetchTargetSavedLiveKpi } = useSectionLiveKpis(targetSavedSectionIds);

// Merge key: the live-KPI section_id is the Section's LocationObject id, matching production-group.location_id.
// The single-Section re-read above is layered on top so its fresher figures win for that one Section,
// without touching every other row's data from the batch query.
const liveKpisBySectionId = computed(() => {
    const map = new Map(liveKpis.value.map((kpi) => [kpi.section_id, kpi]));
    for (const kpi of targetSavedLiveKpisData.value ?? []) map.set(kpi.section_id, kpi);
    return map;
});

const isLoading = computed(
    () =>
        !sitesAreLoaded.value ||
        !sectionsAreLoaded.value ||
        (isQueryEnabled.value && isProductionGroupsLoading.value) ||
        (isQueryEnabled.value && isLiveKpisLoading.value)
);

// Currently open Production Groups keyed by their Section (location_id = Section.id).
// useBarnProductionGroups is a shared query that returns all SPs within the selected date range;
// filter here to those that haven't ended yet so ended Production Groups don't appear as active rows.
function activeProductionGroupsBySectionId(barnEntries: ProductionGroupBarnResponse | undefined): Map<number, ProductionGroup> {
    const map = new Map<number, ProductionGroup>();
    for (const barnEntry of barnEntries ?? []) {
        for (const productionGroup of barnEntry.production_groups ?? []) {
            // Calendar-day comparison, not instant comparison - a Production Group ending "today"
            // must stay visible for the rest of today, not disappear the moment `new Date()`
            // ticks past midnight of its end date.
            if (!productionGroup.end || differenceInCalendarDays(parseISO(productionGroup.end), new Date()) >= 0) {
                map.set(productionGroup.location_id, productionGroup);
            }
        }
    }
    return map;
}

const productionGroupBySectionId = computed(() => activeProductionGroupsBySectionId(productionGroupBarns.value));

// Flat Section view-model list (Module C). Two-pass transformation extracted to composable.
// Each row's `attentionLevel` (Module A) is precomputed once there, rather than independently
// recomputed by card colouring and the sort/group module below.
const { sectionRows } = useSectionRows(physicalSections, productionGroupBySectionId, liveKpisBySectionId, farmsById, barnsById);

// The grid's own row for the open Section, for the ordinary case where the current filters show it.
const openSectionGridRow = computed<SectionRow | null>(() => sectionRows.value.find((row) => row.id === openSectionId.value) ?? null);

// A link can also name a Section the grid is not showing: `sectionRows` is scoped to both the pig
// type and the chosen farms, and a URL knows about neither. Such a Section is resolved here on its
// own terms - from the unfiltered Section list, plus that one Section's live KPIs and that one
// Barn's Production Groups - rather than by moving the reader's pig type and farm chips onto it. The
// overlay is a destination; opening one says nothing about what the rest of the app is filtered to.
//
// Both reads stay disabled while the grid can supply the row itself, so an ordinary open still
// costs no request of its own.
const linkedSection = computed<Section | null>(() => {
    if (openSectionId.value === null || openSectionGridRow.value !== null) return null;
    return (store.state.sections ?? []).find((section) => section.id === openSectionId.value) ?? null;
});
const linkedSectionIds = computed(() => (linkedSection.value ? [linkedSection.value.id] : []));
const linkedBarnIds = computed(() => (linkedSection.value ? [linkedSection.value.barn_id] : []));

const { data: linkedLiveKpisData, isLoading: isLinkedLiveKpisLoading } = useSectionLiveKpis(linkedSectionIds);
const linkedLiveKpisBySectionId = computed(() => new Map((linkedLiveKpisData.value ?? []).map((kpi) => [kpi.section_id, kpi])));

const linkedProductionGroupsEnabled = computed(() => linkedBarnIds.value.length > 0);
const { data: linkedProductionGroupBarns, isLoading: isLinkedProductionGroupsLoading } = useBarnProductionGroups({ barnIds: linkedBarnIds, selectedPeriod }, { enabled: linkedProductionGroupsEnabled });
const linkedProductionGroupBySectionId = computed(() => activeProductionGroupsBySectionId(linkedProductionGroupBarns.value));

// Held back until both reads have settled: a row built from a half-arrived response carries no
// Production Group, which would open the chart on a Section that momentarily looks as though it has
// never had one.
const linkedSections = computed<Section[]>(() => (linkedSection.value === null || isLinkedLiveKpisLoading.value || isLinkedProductionGroupsLoading.value ? [] : [linkedSection.value]));

// The same two-pass row build the grid uses, over the single linked Section - so the overlay and
// the header it shares with the KPI modals receive exactly the row shape they already expect.
const { sectionRows: linkedSectionRows } = useSectionRows(linkedSections, linkedProductionGroupBySectionId, linkedLiveKpisBySectionId, farmsById, barnsById);

// The row the overlay renders: the grid's own where the filters show it, the independently resolved
// one otherwise. Null while either source is still loading, and null for good when the id names no
// Section this user can see - in both cases the grid simply shows through, which is also what a
// hand-edited id should do.
const selectedSection = computed<SectionRow | null>(() => openSectionGridRow.value ?? linkedSectionRows.value[0] ?? null);

// How far back the open Section's own Production Group history is read for the comparison dropdown.
// Wide enough to contain the cycle before the one on screen for every animal group this page covers,
// and no wider - the dropdown offers the immediately preceding period, not a historical archive.
const COMPARISON_HISTORY_MONTHS = 12;

// The open Section's Production Group history, on a window of its own rather than read off the Barn
// response that resolved the row.
//
// The Barn response cannot answer this question. It is windowed by the app-global period picker,
// which this page does not carry - so its window is whatever page the reader came from last set. The
// comparison dropdown offers the Production Group *preceding* the one on screen, and a preceding
// period ends on or before the current one starts, so it lies outside any window that starts at or
// after that start. Whether a Section's predecessor is reachable would come down to where the reader
// had been.
//
// Anchored to the displayed Production Group's start and not to today, because that is what the
// candidate rule measures against: a Section far into a long cycle still has to reach back past its
// predecessor, and a window hung off today would shorten as the cycle ran on.
const comparisonHistoryRequest = computed<ProductionGroupTimelineRequest | null>(() => {
    const section = selectedSection.value;
    if (section === null) return null;
    const anchor = section.productionGroupStart === null ? new Date() : parseISO(section.productionGroupStart);
    return {
        sectionId: section.id,
        barnId: section.barnId,
        from: format(subMonths(anchor, COMPARISON_HISTORY_MONTHS), 'yyyy-MM-dd'),
        to: format(new Date(), 'yyyy-MM-dd'),
    };
});

// `isError` is carried, not swallowed. An empty history and a history that failed to load look
// identical downstream, and the comparison dropdown's empty state reads "no earlier group to compare
// against" - a statement about the Section, made on the strength of a request that never arrived.
const { data: comparisonHistoryData, isError: isComparisonHistoryError } = useProductionGroupTimeline(comparisonHistoryRequest);
const selectedSectionProductionGroupHistory = computed<ProductionGroup[]>(() => comparisonHistoryData.value ?? []);

// Keep the live overview legible: a refresh spins the shared refresh glyph, and any row whose
// displayed live values changed receives a short, non-blocking highlight. The signature excludes
// UI-only state (sort/order/modal state) so selection changes cannot look like new telemetry.
//
// Highlighting is driven by `sectionRows` itself rather than by whatever triggered the refetch, so
// the manual refresh button, the 5-minute background poll, and a Production Group save all go through
// the same path — none of them has to know about highlighting. A change made while the Manage
// Production Group modal is open is queued and revealed only once the modal closes, so the user
// doesn't miss the flash behind the overlay.
const changedSectionIds = ref(new Set<number>());
const highlightTimers = new Map<number, ReturnType<typeof setTimeout>>();
const pendingHighlightIds = new Set<number>();

function rowSignature(section: SectionRow): string {
    return JSON.stringify({
        hasProductionGroup: section.hasProductionGroup,
        pigCount: section.pigCount,
        latestWeightKg: section.latestWeightKg,
        dailyGain: section.dailyGain,
        liveGrowthIndex: section.liveGrowthIndex,
        waterIndex: section.waterIndex,
        mortalityPercent: section.mortalityPercent,
        mortalityCumulativePercent: section.mortalityCumulativePercent,
    });
}

function highlightSection(sectionId: number): void {
    const timer = highlightTimers.get(sectionId);
    if (timer) clearTimeout(timer);

    changedSectionIds.value = new Set(changedSectionIds.value).add(sectionId);
    highlightTimers.set(
        sectionId,
        setTimeout(() => {
            const next = new Set(changedSectionIds.value);
            next.delete(sectionId);
            changedSectionIds.value = next;
            highlightTimers.delete(sectionId);
        }, 1600)
    );
}

const isRefreshing = computed(() => isLiveKpisFetching.value || isBarnKpisFetching.value);

function refreshOverview(): Promise<[unknown, unknown]> {
    return Promise.all([refetchLiveKpis(), refetchBarnKpis()]);
}

// Fires on every `sectionRows` recompute, whatever caused it — a manual refresh, the background
// poll, or a Production Group write invalidating the same queries from inside the modal. Diffs against
// our own last-stable snapshot rather than Vue's built-in previous value: `physicalSections`,
// `useBarnProductionGroups` and `useSectionLiveKpis` settle at different times during initial load, so
// whichever resolves last flips `isLoading` to false in the very same tick it supplies real data —
// diffing against Vue's raw previous tick (still the partial/null pre-load rows) would flash the
// whole table the instant the spinner disappears. Clearing the snapshot while loading means that
// first real tick has no baseline to compare against, so nothing highlights.
let lastStableRows: SectionRow[] | null = null;
watch(sectionRows, (rows) => {
    if (isLoading.value) {
        lastStableRows = null;
        return;
    }

    const previousById = new Map((lastStableRows ?? []).map((row) => [row.id, row]));
    const changedIds = rows
        .filter((row) => {
            const previous = previousById.get(row.id);
            return previous !== undefined && rowSignature(previous) !== rowSignature(row);
        })
        .map((row) => row.id);

    if (isManageModalOpen.value) changedIds.forEach((id) => pendingHighlightIds.add(id));
    else changedIds.forEach((id) => highlightSection(id));

    lastStableRows = rows;
});

watch(isManageModalOpen, (isOpen) => {
    if (isOpen) return;
    pendingHighlightIds.forEach((id) => highlightSection(id));
    pendingHighlightIds.clear();
});

// Timers outlive the row they're animating only by design (1.6s) — but not the component itself.
onUnmounted(() => {
    highlightTimers.forEach((timer) => clearTimeout(timer));
    highlightTimers.clear();
});

// Farm → Barn → Section grouping, sorted within each Barn by the selected sort key (Module B/G).
const farmGroups = computed(() => sortAndGroupSectionsByFarm(sectionRows.value, sortKey.value));

// The rows whose Døde cell can open the mortality detail modal (same guard as the cell's button),
// flattened from the grouped/sorted structure above so prev/next follows the rendered order.
const mortalityDetailSections = computed<SectionRow[]>(() =>
    farmGroups.value.flatMap((farm) =>
        farm.barns.flatMap((barn) => barn.sections.filter((section) => section.hasProductionGroup))
    )
);

// The rows whose Water Norm cell can open the detail modal (same guard as the cell's button),
// flattened from the grouped/sorted structure above so prev/next follows the rendered order.
const waterDetailSections = computed<SectionRow[]>(() =>
    farmGroups.value.flatMap((farm) =>
        farm.barns.flatMap((barn) => barn.sections.filter((section) => section.hasProductionGroup && section.hasWaterSensor))
    )
);

// The rows whose Vægt/Tilvækst/Vækstindeks cells can open the weight detail modal (same guard as
// the cells' buttons — hasWeightSensor, not a per-value check, since each headline card in the
// modal handles its own null state), flattened from the grouped/sorted structure above so
// prev/next follows the rendered order.
const weightDetailSections = computed<SectionRow[]>(() =>
    farmGroups.value.flatMap((farm) =>
        farm.barns.flatMap((barn) => barn.sections.filter((section) => section.hasWeightSensor))
    )
);

// The rows whose chart icon can open the fullscreen detail overlay - unguarded, unlike the KPI
// detail lists above, since the chart affordance stays visible for every Section - flattened from
// the grouped/sorted structure above so prev/next follows the rendered order.
const allDetailSections = computed<SectionRow[]>(() =>
    farmGroups.value.flatMap((farm) => farm.barns.flatMap((barn) => barn.sections))
);

// Card classes for a Section row. Three visually distinct states:
//   - Inactive (no Production Group): dashed outer border, #FBFBFC bg — physically exists, not being monitored.
//   - Ghost (Production Group active, no pigs): dashed outer border, white bg — monitored, but empty.
//   - Active: solid outer border + attention-coloured 4px left border, hover shadow. The row itself
//     is not clickable — the chart icon at the row end opens the detail overlay.
function cardClasses(section: SectionRow): string {
    if (!section.hasProductionGroup) {
        return 'border-dashed border-gray-200 border-l-gray-200 bg-[#FBFBFC]';
    }
    if (section.isEmpty) {
        return 'border-dashed border-gray-200 border-l-gray-200 bg-white';
    }
    const hover = 'hover:shadow-[0_2px_8px_rgba(16,24,40,0.08)]';
    // `attentionLevel` is precomputed per row by useSectionRows (Module A) - read directly here
    // rather than recomputed. An active Alarm is the only signal that lights a card.
    if (section.attentionLevel === 'red') {
        // Pink outer border + deep red left border + warm off-white background.
        return `border-[#F1B5B0] border-l-[#DC2626] bg-[#FFFBFA] ${hover}`;
    }
    // Subtle gray left border signals card identity without demanding attention.
    return `border-gray-200 border-l-[#D8DDE3] bg-white ${hover}`;
}

// Dynamic Mortality colouring: red at or above the mortality target, green below, neutral
// (no colour, same convention as the index columns' `indexColourClass`) when the target
// comparison itself is unknown/uncalculated - that must not silently read as "all good".
function mortalityPercentClass(atOrAboveTarget: boolean | null): string {
    if (atOrAboveTarget === null) return INDEX_COLOUR_NEUTRAL;
    return atOrAboveTarget ? INDEX_COLOUR_RED : INDEX_COLOUR_GREEN;
}

// The mortality figure + threshold flag for the row under the active page-level view (rolling vs
// cumulative). Both are computed once per cell so the template reads a single consistent view.
function sectionMortalityPercent(section: SectionRow): number | null {
    return mortalityPercentForView(section, mortalityView.value);
}

function sectionMortalityAtOrAboveTarget(section: SectionRow): boolean | null {
    return mortalityAtOrAboveTargetForView(section, mortalityView.value);
}

function sectionMortalityPigsToTarget(section: SectionRow): number | null {
    return mortalityPigsToTargetForView(section, mortalityView.value);
}

// The barn aggregate strip's dead count under the active page-level mortality view — same toggle as
// the Døde column (mortalityView), so the two figures always agree on which window they show.
function barnDeadCount(barnId: number): number | null {
    const kpi = barnKpiByBarnId.value.get(barnId);
    if (!kpi) return null;
    return mortalityView.value === 'cumulative' ? kpi.dead_count_since_start : kpi.dead_count_last_7_days;
}

// How many of the barn's sections are missing from the dead count above. The barn's pig count covers
// every section (and every section now shows its own), so only Dead needs the caveat.
function barnSectionsSuppressed(barnId: number): number {
    return barnKpiByBarnId.value.get(barnId)?.sections_suppressed ?? 0;
}

// The graph affordance stays visible for every Section. The stream itself owns the empty state when
// a Section has not started a Production Group or has no readings yet.
function openSection(section: SectionRow) {
    router.push({ name: 'ActiveGroupsDetail', params: { sectionId: String(section.id) } });
}

// Closing returns to the grid's own route. Stepping back unwinds the entry that opened the overlay,
// so Back after closing leaves the page instead of walking straight into the Section again — but the
// overlay can also be arrived at directly from a shared link, where there is no grid behind it to
// step back to, so that case pushes the grid instead.
// Settles only once the navigation has, so a caller that opens something else afterwards is not
// mounting it underneath an overlay still on screen. `router.back()` is `history.go(-1)`: it hands
// back nothing and completes when the browser fires `popstate`, so that branch waits a tick rather
// than on a promise the router does not offer.
async function closeSection(): Promise<void> {
    const previous = window.history.state?.back;
    const gridPath = router.resolve({ name: 'ActiveGroups' }).path;
    if (typeof previous === 'string' && previous.split('?')[0] === gridPath) {
        router.back();
        await nextTick();
        return;
    }
    await router.push({ name: 'ActiveGroups' });
}

// Prev/next inside the overlay replaces rather than pushes: stepping through a barn's Sections is
// browsing one view, not visiting a series of pages, and pushing each one would bury the page the
// user came from under however many Sections they stepped past.
function navigateToSection(section: SectionRow) {
    router.replace({ name: 'ActiveGroupsDetail', params: { sectionId: String(section.id) }, query: route.query });
}

// Alarm handling from inside the mortality detail modal. The multi-alarm list modal sits at the same
// z-index (1030) as the mortality modal and would paint underneath it, so the mortality modal closes
// before the alarm flow opens.
function openAlarmFromMortalityDetail(section: SectionRow, openAlarmModal: CallableFunction) {
    mortalityDetailSection.value = null;
    openAlarm(section, openAlarmModal);
}

// Alarm handling from inside the water detail modal — same z-index handoff as the mortality modal.
function openAlarmFromWaterDetail(section: SectionRow, openAlarmModal: CallableFunction) {
    waterDetailSection.value = null;
    openAlarm(section, openAlarmModal);
}

// Alarm handling from inside the weight detail modal — same z-index handoff as the mortality modal.
function openAlarmFromWeightDetail(section: SectionRow, openAlarmModal: CallableFunction) {
    weightDetailSection.value = null;
    openAlarm(section, openAlarmModal);
}

// Alarm handling from inside the fullscreen section detail overlay — same z-index handoff as the
// other detail views, except that this one closes by navigating. The alarm list modal sits below the
// overlay, so it is opened only once the overlay has gone rather than in the same tick.
async function openAlarmFromSectionDetail(section: SectionRow, openAlarmModal: CallableFunction) {
    await closeSection();
    openAlarm(section, openAlarmModal);
}

// Open the manage Production Group modal for a row. Available on every row regardless of state; the modal
// picks create vs edit mode from the row's `productionGroupId`. Click handler in the template stops
// propagation so the row's own `openSection` does not also fire.
function openManageProductionGroup(section: SectionRow) {
    manageRow.value = section;
    manageProductionGroup.value = productionGroupBySectionId.value.get(section.id) ?? null;
    isManageModalOpen.value = true;
}
</script>

<style scoped>
/* Shared 9-column grid template for the header row and every section card (Module A). Mirrors the
   handoff prototype's --rowcols pattern: a flexible section-name column followed by fixed-ish KPI
   columns and a narrow chart-icon column. The minmax mins guarantee the row never collapses below
   a readable width, so the wrapper scrolls horizontally on narrow viewports. */
.section-grid-row {
    display: grid;
    grid-template-columns:
        minmax(180px, 1.8fr)   /* Section */
        minmax(150px, 1.4fr)   /* Alder — must fit "Week N · Day M" alarm chip */
        minmax(100px, 1fr)     /* Vægt (large bold number) */
        minmax(64px, 0.7fr)    /* Grise */
        minmax(80px, 0.9fr)    /* Døde (large bold + % + indicator) */
        minmax(88px, 1fr)      /* Tilvækst */
        minmax(160px, 1.6fr)   /* Vækstindeks (icon + gauge + value) */
        minmax(160px, 1.6fr)   /* Vandindeks (icon + gauge + value) */
        44px;                  /* Chart icon */
}

/* Explicit update acknowledgement: one low-opacity blue wave crosses the row left-to-right. It
   intentionally leaves no persistent state behind, so attention remains on the live figures. */
.section-recently-updated {
    position: relative;
    overflow: hidden;
}

.section-recently-updated::after {
    position: absolute;
    inset: 0;
    width: 38%;
    content: '';
    pointer-events: none;
    background: linear-gradient(90deg, rgba(191, 219, 254, 0), rgba(147, 197, 253, 0.32), rgba(191, 219, 254, 0));
    transform: translateX(-140%);
    animation: active-groups-update-wave 1.5s ease-out;
}

@keyframes active-groups-update-wave {
    to {
        transform: translateX(365%);
    }
}
</style>
